import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { apiFetch } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { PageTitleWithHelp } from "@/components/ui/title-help";

type Access = "read" | "read_write" | "admin";

type PermissionRow = {
  staffId: number;
  fullName: string;
  active: boolean;
  jobTitle: string | null;
  roleNames: string[];
  modules: { key: string; label: string; access: Access }[];
};

const ACCESS_LABEL: Record<Access, string> = {
  read: "Read",
  read_write: "Read and write",
  admin: "Administrator",
};

const COLUMNS: HubDataTableColumn<PermissionRow>[] = [
  {
    id: "person",
    header: "Person",
    sortValue: (row) => row.fullName,
    cell: (row) => (
      <div>
        <Link className="text-brand-700 hover:underline" to={`/people/staff/${row.staffId}`}>
          {row.fullName}
        </Link>
        {row.jobTitle ? <div className="text-slate-600">{row.jobTitle}</div> : null}
      </div>
    ),
  },
  {
    id: "roles",
    header: "Roles",
    sortValue: (row) => row.roleNames.join(", "),
    cell: (row) => (row.roleNames.length === 0 ? "No hub account" : row.roleNames.join(", ")),
  },
  {
    id: "modules",
    header: "Modules",
    sortValue: (row) => row.modules.map((module) => module.label).join(" "),
    cell: (row) => (
      <div className="flex flex-wrap gap-1">
        {row.modules.map((module) => (
          <span key={module.key} className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-800">
            {module.label}: {ACCESS_LABEL[module.access]}
          </span>
        ))}
      </div>
    ),
  },
];

export function PeoplePermissionsPage() {
  const [search, setSearch] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);

  const permissionsQuery = useQuery({
    queryKey: ["people", "permissions"],
    queryFn: () => apiFetch<{ rows: PermissionRow[] }>("/api/people/permissions"),
  });

  const rows = permissionsQuery.data?.rows ?? [];
  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (activeOnly && !row.active) return false;
      if (needle === "") return true;
      const haystack = [row.fullName, row.jobTitle ?? "", ...row.roleNames, ...row.modules.map((module) => module.label)]
        .join(" ")
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [rows, search, activeOnly]);

  const filtersActive = search.trim() !== "" || !activeOnly;

  return (
    <div className="space-y-4">
      <PageTitleWithHelp hoverHint="This is a read-only view of hub roles and the modules those roles can open. It is not the app onboarding checklist. Change roles on Admin, Hub accounts. An integrator administrator is shown with administrator access on every module.">
        Hub permissions
      </PageTitleWithHelp>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
          <div>
            <Label htmlFor="people-permissions-search">Search permissions</Label>
            <Input
              id="people-permissions-search"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Filter by name, role, or module..."
              className="mt-1"
            />
          </div>
          <Button type="button" size="sm" variant={activeOnly ? "primary" : "secondary"} onClick={() => setActiveOnly((current) => !current)}>
            {activeOnly ? "Active only" : "Including inactive"}
          </Button>
        </CardBody>
      </Card>

      <HubDataTable
        title={permissionsQuery.isLoading ? "People" : `Showing ${visible.length} of ${rows.length} people`}
        columns={COLUMNS}
        rows={visible}
        rowKey={(row) => row.staffId}
        loading={permissionsQuery.isLoading}
        error={permissionsQuery.isError ? (permissionsQuery.error as Error).message : null}
        empty="No people."
        isFiltered={filtersActive}
        filteredEmpty="No people match these filters."
      />
    </div>
  );
}
